import React, { useState, useEffect } from 'react';
import { CheckIcon, ShoppingBagIcon, TruckIcon, StarIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface BusinessType {
  value: string;
  label: string;
}

interface ProductCategory {
  value: string;
  label: string;
}

interface FormData {
  business_name: string;
  business_type: string;
  full_name: string;
  email: string;
  phone_number: string;
  country_region: string;
  city: string;
  state_province: string;
  product_interests: string[];
  additional_information: string;
  shipping_address: string;
}

const WholesaleRegistration: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    business_name: '',
    business_type: 'retailer',
    full_name: '',
    email: '',
    phone_number: '',
    country_region: '',
    city: '',
    state_province: '',
    product_interests: [],
    additional_information: '',
    shipping_address: '',
  });

  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    fetchBusinessTypes();
    fetchProductCategories();
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://139.59.98.59/api';
      const response = await fetch(`${apiUrl}/wholesale-application/business-types`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessTypes(data.data);
      }
    } catch (error) {
      // Error fetching business types
    }
  };

  const fetchProductCategories = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://139.59.98.59/api';
      const response = await fetch(`${apiUrl}/wholesale-application/product-categories`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProductCategories(data.data);
      }
    } catch (error) {
      // Error fetching product categories
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: []
      }));
    }
  };

  const handleProductInterestChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      product_interests: prev.product_interests.includes(value)
        ? prev.product_interests.filter(interest => interest !== value)
        : [...prev.product_interests, value]
    }));

    // Clear error for product interests
    if (errors.product_interests) {
      setErrors(prev => ({
        ...prev,
        product_interests: []
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      alert('Please log in to submit a wholesale application. You can view the form and information, but submission requires authentication.');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://139.59.98.59/api';
      const response = await fetch(`${apiUrl}/wholesale-application/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          alert(data.message || 'Failed to submit application');
        }
      }
    } catch (error) {
      alert('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckIcon className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your wholesale application! Your application is now <strong>pending review</strong> by our admin team. 
              You will receive an email notification once your application has been reviewed and approved. 
              This process typically takes 2-3 business days.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm">
                <strong>Next Steps:</strong> Please wait for admin approval.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-900 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Wholesaler for Shop
            </h1>
            <p className="text-xl lg:text-2xl font-medium mb-4 text-gray-200">
              "Empowering Retailers with Premium Wholesale Deals!"
            </p>
            <p className="text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto">
              Join our exclusive network of retailers and access high-quality, branded clothing at unbeatable wholesale prices. Sign up today to grow your business with Signature Styles!
            </p>
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Introduction Section
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Welcome to <strong>Wholesaler for Shops by Signature Styles</strong> – your trusted source for premium branded clothing at wholesale prices.
              </p>
              <p>
                We specialize in providing high-quality first-copy branded apparel, including <strong>Nike, Adidas, Puma, and more</strong>. Every product is directly imported from top factories in Vietnam, Thailand, and other manufacturing hubs, ensuring you get unmatched quality at competitive prices.
              </p>
              <p>
                By partnering directly with factories, we eliminate intermediaries, allowing us to pass on the savings to you. Whether you're looking for the latest trends, timeless classics, or customized options, we have everything you need to stock your shop with products your customers will love.
              </p>
              <p className="text-lg font-semibold text-gray-900">
                Join us today and gain access to an exclusive range of shorts, T-shirts, tracksuits, and more – all designed to help your retail business thrive!
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <GlobeAltIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Global Sourcing</h3>
              <p className="text-sm text-gray-600">Direct imports from Vietnam, Thailand & more</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <StarIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Premium Quality</h3>
              <p className="text-sm text-gray-600">First-copy branded apparel with precision</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <TruckIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Direct Factory</h3>
              <p className="text-sm text-gray-600">No middlemen, better prices for you</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <ShoppingBagIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Wide Range</h3>
              <p className="text-sm text-gray-600">Shorts, T-shirts, tracksuits & more</p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Benefits of Partnering with Us
            </h2>
            <p className="text-xl text-gray-600">Why Choose Us?</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                <GlobeAltIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Direct Factory Imports</h3>
              <p className="text-gray-600">Quality-assured products directly from Vietnam, Thailand, and more.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                <TruckIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Competitive Wholesale Pricing</h3>
              <p className="text-gray-600">Get the best deals with no middlemen.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                <ShoppingBagIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Wide Range of Products</h3>
              <p className="text-gray-600">Shorts, T-shirts, polos, tracksuits, and more to meet every customer's need.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                <StarIcon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Consistent Quality</h3>
              <p className="text-gray-600">Branded first-copy items made with precision and care.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How to Get Started Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How to Get Started
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Fill out the registration form to join</h3>
                <p className="text-gray-600">Complete our simple wholesale application form below with your business details.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Receive instant access to wholesale pricing</h3>
                <p className="text-gray-600">Get approved and start accessing our exclusive wholesale catalog and pricing.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-xl rounded-lg p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Join Our Wholesale Program</h2>
              <p className="text-gray-600">
                Fill out the form below to get started with exclusive wholesale pricing and access to our premium product catalog.
              </p>
              
              {/* Authentication Status */}
              {!isAuthenticated ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> You can view the form and information below, but you must be logged in to submit your wholesale application. 
                    <a href="/login" className="underline hover:text-blue-900 ml-1">Please log in here</a> or 
                    <a href="/register" className="underline hover:text-blue-900 ml-1">create an account</a> to continue.
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <p className="text-green-800 text-sm">
                    <strong>Welcome, {user?.name || 'User'}!</strong> You are logged in and can submit your wholesale application. 
                    After submission, your application will be reviewed by our admin team for approval.
                  </p>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Name */}
            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-1">
                Enter Your Business Name *
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Enter Your Business Name"
                required
              />
              {errors.business_name && (
                <p className="mt-1 text-sm text-red-600">{errors.business_name[0]}</p>
              )}
            </div>

            {/* Business Type */}
            <div>
              <label htmlFor="business_type" className="block text-sm font-medium text-gray-700 mb-1">
                Business Type: *
              </label>
              <select
                id="business_type"
                name="business_type"
                value={formData.business_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                required
              >
                {businessTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.business_type && (
                <p className="mt-1 text-sm text-red-600">{errors.business_type[0]}</p>
              )}
            </div>

            {/* Full Name */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                Enter Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Enter Full Name"
                required
              />
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-600">{errors.full_name[0]}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Enter Your Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Enter Your Email"
                required
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Phone Number"
                required
              />
              {errors.phone_number && (
                <p className="mt-1 text-sm text-red-600">{errors.phone_number[0]}</p>
              )}
            </div>

            {/* Country/Region */}
            <div>
              <label htmlFor="country_region" className="block text-sm font-medium text-gray-700 mb-1">
                Country/Region: *
              </label>
              <input
                type="text"
                id="country_region"
                name="country_region"
                value={formData.country_region}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Country/Region"
                required
              />
              {errors.country_region && (
                <p className="mt-1 text-sm text-red-600">{errors.country_region[0]}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City: *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Enter Your City"
                required
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city[0]}</p>
              )}
            </div>

            {/* State/Province */}
            <div>
              <label htmlFor="state_province" className="block text-sm font-medium text-gray-700 mb-1">
                State/Province:
              </label>
              <input
                type="text"
                id="state_province"
                name="state_province"
                value={formData.state_province}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="State/Province"
              />
              {errors.state_province && (
                <p className="mt-1 text-sm text-red-600">{errors.state_province[0]}</p>
              )}
            </div>

            {/* Product Interest */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Product Interest: *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {productCategories.map(category => (
                  <label key={category.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.product_interests.includes(category.value)}
                      onChange={() => handleProductInterestChange(category.value)}
                      className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                    />
                    <span className="text-sm text-gray-700">{category.label}</span>
                  </label>
                ))}
              </div>
              {errors.product_interests && (
                <p className="mt-1 text-sm text-red-600">{errors.product_interests[0]}</p>
              )}
            </div>

            {/* Additional Information */}
            <div>
              <label htmlFor="additional_information" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Information (Optional):
              </label>
              <textarea
                id="additional_information"
                name="additional_information"
                value={formData.additional_information}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Additional Comments"
              />
              {errors.additional_information && (
                <p className="mt-1 text-sm text-red-600">{errors.additional_information[0]}</p>
              )}
            </div>

            {/* Shipping Address */}
            <div>
              <label htmlFor="shipping_address" className="block text-sm font-medium text-gray-700 mb-1">
                Shipping Address: *
              </label>
              <textarea
                id="shipping_address"
                name="shipping_address"
                value={formData.shipping_address}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="Shipping Address"
                required
              />
              {errors.shipping_address && (
                <p className="mt-1 text-sm text-red-600">{errors.shipping_address[0]}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading || !isAuthenticated}
                className={`w-full py-3 px-4 rounded-lg transition-colors font-medium ${
                  !isAuthenticated 
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                    : 'bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {loading ? 'Submitting...' : !isAuthenticated ? 'LOGIN REQUIRED TO SUBMIT' : 'SUBMIT APPLICATION'}
              </button>
              
              {!isAuthenticated && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Please <a href="/login" className="text-blue-600 hover:text-blue-800 underline">log in</a> or 
                  <a href="/register" className="text-blue-600 hover:text-blue-800 underline ml-1">register</a> to submit your wholesale application.
                </p>
              )}
            </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleRegistration;
